import * as React from "react"
import { useMutation, useQuery } from "@tanstack/react-query"
import { Check, Download, Loader2, TriangleAlert } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useToast } from "@/components/ui/use-toast"
import { cn } from "@/lib/utils"
import { ApiError } from "@/lib/api"

import {
  descargar,
  fetchCatalogoExport,
  type FiltrosExport,
} from "@/routes/export/api"
import { t } from "@/lib/i18n"

/**
 * EL BOTÓN DE DESCARGAR, uno para todo el producto.
 *
 * Cesar: «debería haber la posibilidad de descargar en Excel o CSV la parte
 * contable, financiera, etc., por tipo inclusive y por tiempo inclusive».
 *
 * ---------------------------------------------------------------------------
 * VA EN LA PANTALLA, NO EN UN «CENTRO DE DESCARGAS»
 *
 * Un centro de descargas obliga a volver a elegir sociedad, libro y rango
 * —los mismos que ya están puestos arriba— y es donde se cuelan las
 * discrepancias: se exporta agosto mientras la pantalla enseña julio, y nadie
 * lo nota hasta que las dos cifras no cuadran. Aquí el botón hereda los filtros
 * de la pantalla en la que está, así que «lo que se ve» y «lo que se baja» son
 * lo mismo por construcción y no por disciplina.
 *
 * ---------------------------------------------------------------------------
 * EL FORMATO SE ELIGE AL PULSAR, no antes
 *
 * Dos entradas en el menú, «Excel» y «CSV», en vez de un selector de formato al
 * lado del botón. Un selector obliga a decidir dos veces —el formato y luego
 * descargar— para algo que casi siempre es «dame el Excel».
 *
 * Y si el servidor no tiene la librería de Excel, la entrada de Excel **no se
 * ofrece** y se dice por qué. Ofrecer un botón que devuelve 500 es peor que no
 * ofrecerlo.
 */
/**
 * El texto que devolvió el backend, no el genérico por código.
 *
 * Un 422 de aquí NOMBRA el filtro que falta —«"Libro diario" necesita
 * entity_id, desde»—, y ése es el mensaje que resuelve el problema. El detalle
 * llega como el cuerpo crudo de la respuesta, que en FastAPI es
 * `{"detail": "..."}`.
 */
function _mensajeDe(error: ApiError): string {
  const crudo = error.detail
  if (typeof crudo === "string" && crudo) {
    try {
      const json = JSON.parse(crudo) as { detail?: unknown }
      if (typeof json.detail === "string") return json.detail
    } catch {
      // No era JSON: sirve tal cual.
    }
    return crudo
  }
  return error.message
}

export function BotonDescargar({
  recurso,
  filtros,
  tenantId,
  etiqueta = "Descargar",
  variant = "outline",
  size = "sm",
  className,
}: {
  /** El `id` del recurso en `/export/catalogo`. */
  recurso: string
  /** Los filtros de ESTA pantalla. Ver el docstring: no se vuelven a pedir. */
  filtros: FiltrosExport
  /**
   * El tenant del dato, cuando no es el activo. Sólo la consola del contador,
   * que ve las declaraciones de toda su cartera sin cambiar de cliente.
   */
  tenantId?: string
  etiqueta?: string
  variant?: "outline" | "ghost" | "default"
  size?: "sm" | "xs" | "default"
  className?: string
}) {
  const { toast } = useToast()

  const catalogo = useQuery({
    queryKey: ["export-catalogo"],
    queryFn: fetchCatalogoExport,
    // El catálogo no cambia durante una sesión: es la forma del producto, no
    // un dato del cliente. Volver a pedirlo en cada pantalla es ruido.
    staleTime: Infinity,
  })

  const bajar = useMutation({
    mutationFn: (formato: "xlsx" | "csv") => descargar(recurso, formato, filtros, tenantId),
    onError: (error) => {
      /*
        UN 422 AQUÍ NOMBRA EL FILTRO QUE FALTA, y hay que mostrarlo tal cual. El
        backend contesta «"Libro diario" necesita entity_id, desde»: traducirlo
        a «no se pudo descargar» manda a buscar el problema al sitio
        equivocado, cuando lo que falta es elegir una sociedad arriba.
      */
      const detalle = error instanceof ApiError
        ? _mensajeDe(error)
        : error instanceof Error ? error.message : String(error)
      toast({
        variant: "warning",
        title: "No pude generar el archivo",
        description: detalle,
      })
    },
  })

  const disponible = catalogo.data?.recursos.some((r) => r.id === recurso) ?? true
  const formatos = catalogo.data?.formatos ?? ["xlsx", "csv"]
  const info = catalogo.data?.recursos.find((r) => r.id === recurso)

  // Un recurso que el servidor no ofrece: el botón no se pinta. Una pantalla
  // que enlaza a un recurso inexistente es un defecto de la SPA, y aparecerá
  // como un botón ausente en vez de como un 404 al pulsar.
  if (catalogo.data && !disponible) return null

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          size={size}
          className={cn(className)}
          disabled={bajar.isPending}
          aria-label={info ? `Descargar ${info.nombre}` : etiqueta}
        >
          {bajar.isPending ? <Loader2 className="animate-spin" /> : <Download />}
          {bajar.isPending ? "Generando…" : etiqueta}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-w-[22rem]">
        {info ? (
          <>
            <DropdownMenuLabel className="font-medium">{info.nombre}</DropdownMenuLabel>
            <p className="px-2 pb-1.5 text-2xs leading-4 text-muted-foreground">
              {info.descripcion}
            </p>
            <DropdownMenuSeparator />
          </>
        ) : null}

        {formatos.includes("xlsx") ? (
          <DropdownMenuItem onSelect={() => bajar.mutate("xlsx")}>
            <Check className="opacity-0" />
            {t("Excel (.xlsx)")}</DropdownMenuItem>
        ) : null}
        <DropdownMenuItem onSelect={() => bajar.mutate("csv")}>
          <Check className="opacity-0" />
          {t("CSV")}</DropdownMenuItem>

        {catalogo.data?.nota_xlsx ? (
          <p className="flex gap-1.5 px-2 pt-1.5 text-2xs leading-4 text-warning">
            <TriangleAlert className="mt-px h-3 w-3 shrink-0" aria-hidden="true" />
            {catalogo.data.nota_xlsx}
          </p>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
